import { useEffect, useState } from 'react'
import { X, Sparkles, Loader2, RotateCcw } from 'lucide-react'
import { useDashboardStore } from '../stores/dashboardStore'
import { useOrderDetail } from '../hooks/useOrderDetail'
import { useOrderSummary } from '../hooks/useOrderSummary'
import { useResetOrderStatus } from '../hooks/useResetOrderStatus'
import { useUserOrganization } from '@/hooks/useUserOrganization'
import { getStatusStyle, getStatusLabel } from '../utils/statusColors'
import { canResetOrder, isResetReasonValid } from '../utils/resetOrderDecision'
import { formatOrderDate } from '../utils/formatters'
import { OrderInfoStrip } from './OrderInfoStrip'
import { OrderTimeline } from './OrderTimeline'
import { SkeletonTimeline } from './SkeletonTimeline'
import { EmptyDetailPlaceholder } from './EmptyDetailPlaceholder'

function DetailSkeleton() {
  return (
    <div className="flex flex-col h-full animate-pulse">
      {/* Header skeleton */}
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex items-center gap-2">
          <div className="h-6 w-20 bg-gray-200 rounded" />
          <div className="h-5 w-16 bg-gray-200 rounded-full" />
        </div>
        <div className="h-3.5 w-48 bg-gray-100 rounded mt-2" />
      </div>

      {/* Info strip skeleton */}
      <div className="flex items-center gap-3 px-6 py-3 border-b border-gray-200">
        <div className="h-6 w-24 bg-gray-200 rounded-full" />
        <div className="h-6 w-20 bg-gray-200 rounded-full" />
        <div className="h-6 w-28 bg-gray-200 rounded-full" />
        <div className="h-6 w-16 bg-gray-200 rounded-full" />
      </div>

      {/* Timeline skeleton */}
      <div className="flex-1 overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-3 border-b border-gray-200">
          <div className="h-3 w-24 bg-gray-200 rounded" />
        </div>
        <div className="px-6 pb-12">
          <SkeletonTimeline />
        </div>
      </div>
    </div>
  )
}

interface OrderDetailPanelProps {
  orderId?: number | null
}

export function OrderDetailPanel({ orderId: orderIdProp }: OrderDetailPanelProps) {
  const storeOrderId = useDashboardStore((s) => s.selectedOrderId)
  const activeOrderId = orderIdProp !== undefined ? orderIdProp : storeOrderId

  // Keying on the order remounts the panel when the selection changes, which
  // discards the reset-panel state. Previously an effect cleared that state
  // with setState, which costs an extra render pass with the old order's
  // panel still open.
  return <OrderDetail key={activeOrderId ?? 'none'} activeOrderId={activeOrderId} />
}

function OrderDetail({ activeOrderId }: { activeOrderId: number | null }) {
  const selectOrder = useDashboardStore((s) => s.selectOrder)

  const { data: order, isLoading, isError, refetch } = useOrderDetail(activeOrderId)
  const { state: summaryState, summarize, dismiss: dismissSummary, onOrderChange } = useOrderSummary()
  const resetMutation = useResetOrderStatus()
  const { data: org } = useUserOrganization()
  const [showResetPanel, setShowResetPanel] = useState(false)
  const [resetReason, setResetReason] = useState('')

  useEffect(() => {
    onOrderChange(activeOrderId ?? null)
  }, [activeOrderId, onOrderChange])

  if (!activeOrderId) {
    return <EmptyDetailPlaceholder />
  }

  if (isLoading) {
    return <DetailSkeleton />
  }

  if (isError) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-2">
        <p className="text-sm text-gray-400">Kunne ikke laste ordre #{activeOrderId}</p>
        <button onClick={() => refetch()} className="text-sm text-emerald-600 hover:underline">
          Prøv igjen
        </button>
      </div>
    )
  }

  if (!order) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-sm text-gray-400">Fant ikke ordre #{activeOrderId}</p>
      </div>
    )
  }

  const status = getStatusStyle(order.app_status)

  return (
    <div className="flex flex-col h-full" data-detail-panel>
      {/* Header */}
      <div className="flex items-start justify-between px-6 py-4 border-b border-gray-200">
        <div>
          <div className="flex items-center gap-2">
            <span className="text-xl font-bold text-gray-900">#{order.order_id}</span>
            <span className={`text-[11px] font-medium px-2 py-0.5 rounded-full ${status.bg} ${status.text}`}>
              {getStatusLabel(order.app_status)}
            </span>
          </div>
          <p className="text-[13px] text-gray-500 mt-0.5">
            {order.customer_name ?? 'Ukjent kunde'}
            {order.order_owner_customer_name && ` · ${order.order_owner_customer_name}`}
            {order.order_date && ` · Opprettet ${formatOrderDate(order.order_date)}`}
          </p>
        </div>
        <div className="flex items-center gap-1">
          {canResetOrder(org?.role, order.app_status) && (
            <button
              onClick={() => setShowResetPanel(!showResetPanel)}
              className="w-7 h-7 flex items-center justify-center rounded-md text-amber-500 hover:text-amber-700 hover:bg-amber-50 transition-colors"
              title="Tilbakestill ordre"
            >
              <RotateCcw size={16} />
            </button>
          )}
          <button
            onClick={() => summarize(activeOrderId)}
            disabled={summaryState.status === 'loading'}
            className="w-7 h-7 flex items-center justify-center rounded-md text-indigo-500 hover:text-indigo-700 hover:bg-indigo-50 transition-colors disabled:opacity-50"
            title="Oppsummer"
          >
            {summaryState.status === 'loading' ? (
              <Loader2 size={16} className="animate-spin" />
            ) : (
              <Sparkles size={16} />
            )}
          </button>
          <button
            onClick={() => selectOrder(null)}
            className="w-7 h-7 flex items-center justify-center rounded-md text-gray-400 hover:text-gray-900 hover:bg-gray-100 transition-colors"
            title="Lukk"
          >
            <X size={18} />
          </button>
        </div>
      </div>

      {/* Reset panel */}
      {showResetPanel && (
        <div className="px-6 py-3 border-b border-gray-200">
          <div className="bg-amber-50 border border-amber-200 rounded-md p-3">
            <p className="text-sm font-medium text-amber-800 mb-2">
              Tilbakestill ordre til &laquo;Importert&raquo;
            </p>
            <p className="text-xs text-amber-700 mb-3">
              Ordren nullstilles og all utleveringsdata slettes. Kun auto-leverte ordrer kan tilbakestilles.
            </p>
            <textarea
              value={resetReason}
              onChange={(e) => setResetReason(e.target.value)}
              placeholder="Hvorfor skal ordren tilbakestilles? (min. 10 tegn)"
              className="w-full text-sm border border-amber-300 rounded-md p-2 mb-2 resize-none focus:outline-none focus:ring-1 focus:ring-amber-400"
              rows={2}
            />
            <div className="flex items-center gap-2">
              <button
                onClick={() => {
                  resetMutation.mutate(
                    { orderId: order.order_id, reason: resetReason.trim() },
                    {
                      onSuccess: () => {
                        setShowResetPanel(false)
                        setResetReason('')
                      },
                    },
                  )
                }}
                disabled={!isResetReasonValid(resetReason) || resetMutation.isPending}
                className="px-3 py-1.5 text-xs font-medium text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {resetMutation.isPending ? 'Tilbakestiller...' : 'Tilbakestill'}
              </button>
              <button
                onClick={() => {
                  setShowResetPanel(false)
                  setResetReason('')
                }}
                className="px-3 py-1.5 text-xs font-medium text-gray-600 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
              >
                Avbryt
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Info strip */}
      <OrderInfoStrip order={order} />

      {/* AI Summary */}
      {summaryState.status === 'success' && (
        <div className="px-6 py-3 border-b border-gray-200">
          <div className="bg-indigo-50 border border-indigo-200 rounded-md p-3 relative">
            <button
              onClick={dismissSummary}
              className="absolute top-2 right-2 w-5 h-5 flex items-center justify-center rounded text-indigo-400 hover:text-indigo-700 hover:bg-indigo-100 transition-colors"
            >
              <X size={14} />
            </button>
            <p className="text-[11px] font-semibold text-indigo-500 uppercase tracking-wider mb-1.5 flex items-center gap-1">
              <Sparkles size={12} />
              AI-oppsummering
            </p>
            <p className="text-sm text-indigo-900 leading-relaxed pr-5">
              {summaryState.data.oppsummering}
            </p>
            {summaryState.data.spor_om && summaryState.data.spor_om.length > 0 && (
              <div className="mt-2 pt-2 border-t border-indigo-200">
                <p className="text-[11px] font-semibold text-indigo-500 uppercase tracking-wider mb-1">Spør om</p>
                <ul className="text-sm text-indigo-800 space-y-0.5">
                  {summaryState.data.spor_om.map((q, i) => (
                    <li key={i} className="flex gap-1.5">
                      <span className="text-indigo-400 shrink-0">•</span>
                      <span>{q}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      )}

      {summaryState.status === 'error' && (
        <div className="px-6 py-3 border-b border-gray-200">
          <div className="bg-red-50 border border-red-200 rounded-md p-3 relative">
            <button
              onClick={dismissSummary}
              className="absolute top-2 right-2 w-5 h-5 flex items-center justify-center rounded text-red-400 hover:text-red-700 hover:bg-red-100 transition-colors"
            >
              <X size={14} />
            </button>
            <p className="text-sm text-red-700">{summaryState.error}</p>
          </div>
        </div>
      )}

      {/* Info/notat */}
      {order.info && (
        <div className="px-6 py-3 border-b border-gray-200">
          <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-1">Notat</p>
          <div className="bg-gray-50 border border-gray-200 rounded-md p-3 text-sm text-gray-700 leading-relaxed">
            {order.info}
          </div>
        </div>
      )}

      {/* Timeline */}
      <div className="flex-1 overflow-y-auto">
        <OrderTimeline orderId={activeOrderId} />
      </div>
    </div>
  )
}
